import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./scannedCard.css";
import { Box, Button, Typography } from "@mui/material";
import CustomTooltip from "../../CustomTooltip";
import ImageModal from "../../modals/imageModal/ImageModal";
import LogoutandDeleteModal from "../../modals/LogoutandDeleteModal/LogoutandDeleteModal";
import SingleModal from "../../modals/singleModal/SingleModal";
import Card from "../../card-templates/Card";
import { getIcon } from "../../../../utils/iconConfig";
import { ReactComponent as QuantityIcon } from "../../../../assets/card-icons/quantity_Icon.svg";
import { useGlobalContext } from "../../../../context/GlobalContext";
import Image from "../../../../assets/images/default-img.jpg";

const ScannedCard = ({ data, onDelete }) => {
  const [details, setDetails] = useState([]);
  const {
    selectedCard,
    setSelectedCard,
    setCartData,
    cartData,
    handleAddToCart,
    handleRemoveFromCart,
  } = useGlobalContext();

  const [modifyModal, setModifyModal] = useState({ open: false, data: null });

  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imgUrl: null,
    styleNumber: null,
    allData: null,
  });

  const [currentIndex, setCurrentIndex] = useState(
    cartData?.findIndex((item) => item?.styleNumber === data?.styleNumber)
  );

  const [quantity, setQuantity] = useState(data?.quantity);

  const isFirstItem = currentIndex === 0;

  const isLastItem =
    currentIndex === cartData?.length - 1 || cartData?.length === 0;

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    data: null,
  });

  const handleOpenSingleModal = useCallback((data) => {
    setModifyModal({ open: true, data });
  }, []);

  const handleCloseSingleModal = useCallback((data) => {
    setModifyModal({ open: false, data });
  }, []);

  // Keeps the stored cart in step with the quantity input, growing or
  // shrinking quantityData to match. Returns early when the stored item
  // already agrees, which is always the case on mount - previously, adding one
  // design rewrote and republished the whole cart twice just because a new
  // card had appeared.
  useEffect(() => {
    const nextQuantity = parseInt(quantity);
    if (!nextQuantity) return;

    const cartItems = JSON.parse(localStorage.getItem("cartItems")) || [];

    const currentCartItem = cartItems.find(
      (item) => item?.styleNumber === data?.styleNumber
    );
    if (!currentCartItem) return;

    if (
      currentCartItem?.quantity === nextQuantity &&
      (currentCartItem?.quantityData?.length || 0) === nextQuantity
    ) {
      return;
    }

    const updatedCart = cartItems.map((item) => {
      if (item?.styleNumber !== data?.styleNumber) {
        return item;
      }

      const quantityData = [...(item?.quantityData || [])];

      if (nextQuantity > quantityData.length) {
        const toAdd = nextQuantity - quantityData.length;
        for (let i = 0; i < toAdd; i++) {
          quantityData?.push({
            key: `${item?.styleNumber}-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`,
            itemSize: item?.itemSize || "",
            goldColor: "P",
            goldQty: "18",
            diaQty: "VS-GH",
            message: null,
          });
        }
      } else if (nextQuantity < quantityData.length) {
        quantityData.splice(nextQuantity);
      }

      return {
        ...item,
        quantity: nextQuantity,
        quantityData: quantityData,
      };
    });

    localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    setCartData(updatedCart);
  }, [quantity]);

  const showDetails = useMemo(
    () => [
      {
        label: data?.styleNumber,
        Icon: getIcon(data?.designClass || "Gold"),
        tooltipTitle: null,
      },
      {
        label: `${data?.diaWeight} Ct / ${data?.diaPcs} Pcs`,
        Icon: getIcon("Diamond"),
        tooltipTitle: "Diamond Weight / Diamond Pieces",
      },
      {
        label: `${data?.netWeight} g / ${data?.grossWeight} g `,
        Icon: getIcon("GoldBar"),
        tooltipTitle: "Gold Net Weight / Gold Gross Weight",
      },
      {
        label: `${data?.quantityData?.[0]?.itemSize || "N/A"}`,
        Icon: getIcon("Scale"),
        tooltipTitle: "Size",
      },
    ],
    [data]
  );

  useEffect(() => {
    setDetails(showDetails);
  }, [data]);

  const handleOpenImageModal = useCallback((imgUrl, styleNumber, data) => {
    setImageModal({ isOpen: true, imgUrl, styleNumber, allData: data });
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setImageModal({ isOpen: false, imgUrl: null, styleNumber: null });
  }, []);

  const handleOpenDeleteModal = useCallback((data) => {
    setDeleteModal({ open: true, data });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModal({ open: false, data: null });
  }, []);

  const handleCardClick = () => {
    setSelectedCard(data?.styleNumber);
  };

  const handleNextClick = () => {
    if (currentIndex < cartData?.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const nextItem = cartData[newIndex];

      setImageModal({
        isOpen: true,
        imgUrl: nextItem?.imageUrl || null,
        styleNumber: nextItem?.styleNumber,
        allData: nextItem,
      });
    }
  };

  const handlePrevClick = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const prevItem = cartData[newIndex];

      setImageModal({
        isOpen: true,
        imgUrl: prevItem?.imageUrl || null,
        styleNumber: prevItem?.styleNumber,
        allData: prevItem,
      });
    }
  };

  return (
    <>
      <Card
        width={"21vw"}
        height={"10.5vw"}
        style={{
          borderRadius: ".5vw",
          padding: ".75vw",

          border:
            selectedCard === data.styleNumber ? ".05vw solid #C0C0C0" : "none",
          cursor: "pointer",
        }}
        sx={{ gap: "0px" }}
        onClick={handleCardClick}
      >
        <Box
          sx={{ display: "flex", justifyContent: "space-between" }}
          width={"100%"}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between" }}
            width={"100%"}
          >
            <Box className="left" sx={{ gap: ".6vw" }}>
              {details.map((detail, index) => (
                <Box key={index} className={`content-new content-new-${index}`}>
                  {detail.Icon}
                  <CustomTooltip title={detail?.tooltipTitle}>
                    <Typography
                      className={`content-typo content-typo-${index}`}
                      ml={".5vw"}
                      fontSize={".7vw"}
                    >
                      {detail.label}
                    </Typography>
                  </CustomTooltip>
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: ".6vw" }}>
                <Typography
                  sx={{
                    fontFamily: "Karla",
                    fontSize: ".75vw",
                    fontWeight: "500",
                  }}
                >
                  <QuantityIcon />
                </Typography>
                <input
                  type="tel"
                  maxLength={3}
                  value={quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,5}$/.test(value)) {
                      const numericValue =
                        value === ""
                          ? ""
                          : Math.min(200, Math.max(1, Number(value)));
                      setQuantity(value === "" ? "" : numericValue.toString());
                    }
                  }}
                  onBlur={() => {
                    if (quantity === "") setQuantity("1");
                  }}
                  style={{
                    width: "2.2vw",
                    border: "none",
                    height: "1vw",
                    maxheight: "1.85vw",
                    fontSize: ".6vw",
                    textAlign: "center",
                    borderRadius: ".2vw",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: ".5vw",
            }}
          >
            <span
              style={{
                display: "flex",
                cursor: "pointer",
              }}
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDeleteModal(data);
                }}
                style={{ height: "1.1vw" }}
              >
                {getIcon("dustbin")}
              </span>
            </span>
            <Box
              style={{
                width: "fit-content",
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img
                className="card-image"
                style={{
                  width: "8vw",
                  height: "8vw",
                }}
                src={data?.transformedImageUrl || Image}
                alt={data?.styleNumber}
                onClick={() =>
                  handleOpenImageModal(
                    data?.imageUrl ? data?.imageUrl : null,
                    data?.styleNumber,
                    data
                  )
                }
              />
              <Button
                variant="outlined"
                color="success"
                sx={{
                  position: "absolute",
                  bottom: "0vw",
                  background: "#FFFFFF",
                  border: ".05vw solid #50C878",
                  borderRadius: ".25vw",
                  color: "#50C878",
                  fontWeight: "700",
                  textTransform: "none",
                  // px: 2,
                  minWidth: 0,
                  padding: 0,
                  fontFamily: "Karla",
                  fontSize: ".75vw",
                  marginInline: "auto",
                  // alignSelf:'center',
                  ":hover": {
                    background: "#FFFFFF",
                  },

                  width: "80%",
                  height: "3.5vh",
                }}
                onClick={() => handleOpenSingleModal(data)}
              >
                Modify
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        ></Box>
      </Card>

      {/* Mounted only while actually open. Every card in the sidebar used to
          keep all three of these mounted, and ImageModal's isAdded prop
          scanned the entire cart on every card even while closed. */}
      {modifyModal.open && (
        <SingleModal
          data={modifyModal.data}
          openModal={modifyModal.open}
          handleClose={handleCloseSingleModal}
        />
      )}

      {imageModal.isOpen && (
        <ImageModal
          openModal={imageModal.isOpen}
          handleClose={handleCloseImageModal}
          data={imageModal}
          nextClick={() => handlePrevClick()}
          prevClick={() => handleNextClick()}
          isAdded={cartData?.some(
            (item) =>
              String(item?.styleNumber)?.trim()?.toLowerCase() ===
              String(imageModal?.styleNumber)?.trim()?.toLowerCase()
          )}
          setIsAdded={(value) => {
            // Handle add/remove based on value
            const currentDesign = cartData.find(
              (d) =>
                String(d?.styleNumber).trim().toLowerCase() ===
                String(imageModal?.styleNumber).trim().toLowerCase()
            );
            if (currentDesign) {
              value
                ? handleAddToCart(currentDesign)
                : handleRemoveFromCart(currentDesign);
            }
          }}
          hasNext={!isFirstItem}
          hasPrev={!isLastItem}
        />
      )}

      {deleteModal.open && (
        <LogoutandDeleteModal
          openModal={deleteModal.open}
          handleClose={handleCloseDeleteModal}
          deleteItem={deleteModal?.data}
          onAction={onDelete}
          modalType={"delete"}
        />
      )}
    </>
  );
};

export default ScannedCard;

